<script setup>
import { ref, computed, watch, getCurrentInstance } from 'vue';
import { usePage } from '@inertiajs/vue3';
import Dialog from 'primevue/dialog';
import Button from 'primevue/button';

const { proxy } = getCurrentInstance();
const $t = proxy.$t;

const page = usePage();
const comingSoonConfig = computed(() => page.props.paymentMethodsComingSoon ?? {});

const props = defineProps({
    visible: {
        type: Boolean,
        required: true,
    },
    submitting: {
        type: Boolean,
        default: false,
    },
    subtotal: {
        type: Number,
        default: 0,
    },
    currency: {
        type: String,
        default: 'USD',
    },
    paymentTaxes: {
        type: Object,
        default: () => ({ stripe: 0, pix: 0, boleto: 0, wise: 0 }),
    },
});

const emit = defineEmits(['update:visible', 'confirm']);

const selectedMethod = ref(null);

const METHODS = computed(() => {
    const upper = (props.currency || '').toUpperCase();
    const cs = comingSoonConfig.value;

    const def = [
        { key: 'stripe', label: 'Stripe',     sublabel: $t('Credit / debit card'),        approval: $t('Instant'),         currencies: ['BRL', 'USD', 'EUR'], icon: 'pi-credit-card', iconClass: 'text-indigo-600', bgClass: 'bg-indigo-50' },
        { key: 'pix',    label: 'Pix',        sublabel: $t('Instant transfer'),           approval: $t('Instant'),         currencies: ['BRL'],               icon: 'pi-qrcode',      iconClass: 'text-green-600',  bgClass: 'bg-green-50'  },
        { key: 'boleto', label: 'Boleto',     sublabel: $t('Bank slip'),                  approval: $t('1–3 business days'), currencies: ['BRL'],             icon: 'pi-barcode',     iconClass: 'text-orange-600', bgClass: 'bg-orange-50' },
        { key: 'wise',   label: 'Wise',       sublabel: $t('International transfer'),     approval: $t('1–2 business days'), currencies: ['USD', 'EUR'],      icon: 'pi-send',        iconClass: 'text-blue-600',   bgClass: 'bg-blue-50'   },
        { key: 'manual', label: $t('Manual'), sublabel: $t('Manual payment / agreement'), approval: $t('Manual review'),   currencies: ['BRL', 'USD', 'EUR'], icon: 'pi-wallet',      iconClass: 'text-gray-600',   bgClass: 'bg-gray-50'   },
    ];

    return def.map(m => {
        const currencyOk = m.currencies.includes(upper);
        const comingSoon = !!cs[m.key];
        const enabled = currencyOk && !comingSoon;

        let disabledReason = null;
        if (comingSoon) {
            disabledReason = $t('Coming soon');
        } else if (!currencyOk) {
            disabledReason = $t('Not available in {{currency}}. Switch currency to use this method.', { currency: upper });
        }

        return { ...m, enabled, comingSoon, currencyOk, disabledReason };
    });
});

const roundWithCents = (value) => Math.ceil(value * 20) / 20;

const taxAmount = computed(() => {
    if (!selectedMethod.value) return 0;
    const rate = props.paymentTaxes[selectedMethod.value] ?? 0;
    return props.subtotal * (rate / 100);
});

// Sum rounded display values so total matches subtotal + tax as displayed
const total = computed(() => roundWithCents(props.subtotal) + roundWithCents(taxAmount.value));

const selectedTaxRate = computed(() => {
    if (!selectedMethod.value) return 0;
    return props.paymentTaxes[selectedMethod.value] ?? 0;
});

const formatCurrency = (value) => {
    const rounded = Math.ceil(value * 20) / 20;
    return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: props.currency,
        minimumFractionDigits: 2,
    }).format(rounded);
};

watch(() => props.visible, (val) => {
    if (!val) selectedMethod.value = null;
});

watch(() => props.currency, () => {
    const current = METHODS.value.find(m => m.key === selectedMethod.value);
    if (current && !current.enabled) selectedMethod.value = null;
});

const confirm = () => {
    if (!selectedMethod.value) return;
    emit('confirm', selectedMethod.value);
};

const close = () => emit('update:visible', false);
</script>

<template>
    <Dialog
        :visible="visible"
        @update:visible="$emit('update:visible', $event)"
        modal
        :closable="!submitting"
        :style="{ width: '480px' }"
        :header="$t('Choose Payment Method')"
    >
        <div class="flex flex-col gap-3 py-2">
            <!-- Payment method cards -->
            <div
                v-for="method in METHODS"
                :key="method.key"
                v-tooltip.top="!method.enabled ? method.disabledReason : null"
            >
                <button
                    class="payment-card"
                    :class="{
                        'payment-card--selected': selectedMethod === method.key,
                        'payment-card--disabled': !method.enabled,
                    }"
                    :disabled="!method.enabled || submitting"
                    @click="selectedMethod = method.key"
                >
                    <div class="payment-card-icon" :class="method.bgClass">
                        <i :class="`pi ${method.icon} text-2xl ${method.iconClass}`"></i>
                    </div>

                    <div class="flex-1 text-left min-w-0">
                        <div class="flex items-center gap-2 flex-wrap">
                            <span class="font-semibold text-surface-900 dark:text-surface-0">{{ method.label }}</span>
                            <span v-if="method.comingSoon" class="text-[10px] font-semibold uppercase tracking-wide px-1.5 py-0.5 rounded bg-surface-100 text-surface-500">
                                {{ $t('Coming soon') }}
                            </span>
                            <span v-else-if="!method.currencyOk" class="text-[10px] font-semibold uppercase tracking-wide px-1.5 py-0.5 rounded bg-amber-100 text-amber-700">
                                {{ $t('Switch currency') }}
                            </span>
                        </div>

                        <div class="text-sm text-surface-500 truncate">{{ method.sublabel }}</div>

                        <div class="flex items-center gap-1.5 mt-0.5 text-[11px] text-surface-400">
                            <i class="pi pi-clock text-[10px]"></i>
                            <span>{{ method.approval }}</span>
                            <span class="opacity-50">·</span>
                            <span>{{ method.currencies.join(' · ') }}</span>
                            <template v-if="method.enabled && paymentTaxes[method.key] > 0">
                                <span class="opacity-50">·</span>
                                <span>{{ $t('Tax') }} {{ paymentTaxes[method.key] }}%</span>
                            </template>
                        </div>
                    </div>

                    <i
                        class="pi text-lg transition-colors shrink-0"
                        :class="selectedMethod === method.key ? 'pi-check-circle text-primary' : 'pi-circle text-surface-300'"
                    ></i>
                </button>
            </div>

            <!-- Resumo de valores -->
            <div v-if="selectedMethod" class="mt-2 p-3 bg-surface-50 dark:bg-surface-800 rounded-lg border border-surface-200 text-sm space-y-1">
                <div class="flex justify-between text-surface-600">
                    <span>{{ $t('Subtotal') }}</span>
                    <span>{{ formatCurrency(subtotal) }}</span>
                </div>
                <div class="flex justify-between text-surface-600">
                    <span>{{ $t('Tax') }} ({{ selectedTaxRate }}%)</span>
                    <span>{{ formatCurrency(taxAmount) }}</span>
                </div>
                <div class="flex justify-between font-semibold text-surface-900 dark:text-surface-0 border-t border-surface-200 pt-1 mt-1">
                    <span>{{ $t('Total') }}</span>
                    <span class="text-primary">{{ formatCurrency(total) }}</span>
                </div>
            </div>
        </div>

        <template #footer>
            <div class="flex justify-between items-center w-full">
                <Button
                    :label="$t('Cancel')"
                    severity="secondary"
                    text
                    :disabled="submitting"
                    @click="close"
                />
                <Button
                    :label="$t('Confirm Payment')"
                    icon="pi pi-lock"
                    :loading="submitting"
                    :disabled="!selectedMethod || submitting"
                    @click="confirm"
                />
            </div>
        </template>
    </Dialog>
</template>

<style scoped>
.payment-card {
    display: flex;
    align-items: center;
    gap: 1rem;
    padding: 0.875rem 1rem;
    border: 2px solid var(--surface-200);
    border-radius: 0.75rem;
    background: var(--surface-0);
    cursor: pointer;
    transition: border-color 0.15s, box-shadow 0.15s;
    width: 100%;
    text-align: left;
}

.payment-card:hover:not(.payment-card--disabled):not(:disabled) {
    border-color: var(--primary-color);
}

.payment-card--selected {
    border-color: var(--primary-color);
    background: var(--primary-50, #f0f7ff);
}

.payment-card--disabled {
    opacity: 0.55;
    cursor: not-allowed;
}

.payment-card-icon {
    width: 2.75rem;
    height: 2.75rem;
    border-radius: 0.5rem;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
}
</style>
